import { CardOnboardingResponse, PendingRequirement } from '../../../../queries/pxtraCard';

/**
 * The current stand in one sentence. The backend sends codes, never German
 * prose — the dashboard ships de and en.
 */
export type OnboardingSentence = {
  key: string;
  requirement?: PendingRequirement;
};

type SentenceInput = Pick<
  CardOnboardingResponse,
  'phase' | 'action_required_by_company' | 'pending_requirement' | 'steps'
>;

const stepIsOpen = (onboarding: SentenceInput, id: string) =>
  onboarding.steps?.some((step) => step.id === id && step.state === 'todo') ?? false;

export const onboardingSentence = (onboarding: SentenceInput): OnboardingSentence => {
  if (onboarding.phase === 'refused') {
    return { key: 'pxtraCard:onboardingRefusedSentence' };
  }

  if (onboarding.phase === 'not_started') {
    return { key: 'pxtraCard:onboardingNotStartedSentence' };
  }

  if (onboarding.action_required_by_company) {
    // Swan names a concrete requirement only while it waits for information.
    // Before that the move is still the company's — the form is unfinished —
    // and claiming Swan is checking would contradict the steps below.
    if (onboarding.pending_requirement) {
      return {
        key: 'pxtraCard:onboardingWaitingOnYou',
        requirement: onboarding.pending_requirement
      };
    }

    // Swan only starts checking once the legal representative has signed, so
    // between the finished form and the signature the move is still theirs.
    return stepIsOpen(onboarding, 'identification')
      ? { key: 'pxtraCard:onboardingWaitingOnIdentification' }
      : { key: 'pxtraCard:onboardingWaitingOnYourForm' };
  }

  return { key: 'pxtraCard:onboardingSwanChecking' };
};

/**
 * The word after "Einrichtung der pxtra Card:" in the notice title. It reads
 * the same stand as the sentence below it and the chips on the checklist —
 * the raw KYB status does not: Swan says "waiting for information" for a tax
 * id as much as for documents, and "not started" while the form is open.
 */
export type TitleStatus = { key: string; params?: Record<string, string> };

export const onboardingTitleStatus = (
  onboarding: Pick<CardOnboardingResponse, 'phase' | 'action_required_by_company' | 'steps'>
): TitleStatus => {
  if (onboarding.phase === 'not_started') return { key: 'pxtraCard:verification_not_started' };

  if (onboarding.phase === 'refused') return { key: 'pxtraCard:verification_refused' };

  if (!onboarding.action_required_by_company) return { key: 'pxtraCard:stepStateWaiting' };

  // When the only open step is somebody else's signature, "on you" names
  // the wrong person; the chip on the checklist already says whose it is.
  const open = onboarding.steps?.filter((step) => step.state === 'todo') ?? [];
  const [only] = open;

  if (
    open.length === 1 &&
    only.id === 'identification' &&
    only.payload.representation === 'other' &&
    only.payload.name
  ) {
    return { key: 'pxtraCard:stepStateTodoOther', params: { name: only.payload.name } };
  }

  return { key: 'pxtraCard:stepStateTodo' };
};

export const isOnboardingOpen = (onboarding?: CardOnboardingResponse): boolean =>
  !!onboarding && onboarding.phase !== 'verified';

export type OnboardingOutcome = 'verified' | 'refused';

/**
 * Swan decides while the manager is elsewhere in the dashboard, and the poll
 * is what notices. Only a turn counts: a page opened on a settled stand has
 * nothing new to say.
 */
export const onboardingOutcome = (
  previous: Pick<CardOnboardingResponse, 'phase'> | undefined,
  next: Pick<CardOnboardingResponse, 'phase'>
): OnboardingOutcome | undefined => {
  if (!previous || previous.phase === next.phase) return undefined;

  if (next.phase === 'verified' || next.phase === 'refused') return next.phase;

  return undefined;
};

/**
 * The tax id outlives verification: Germany grants 90 days from account
 * opening, and missing it suspends the account with its IBAN and cards. So the
 * dot stays on the entry even once everything else is done.
 */
export const hasOpenTaxDeadline = (onboarding?: CardOnboardingResponse): boolean =>
  !!onboarding?.steps.some((step) => step.id === 'tax_id' && step.state !== 'done');

export const needsCompanyAction = (onboarding?: CardOnboardingResponse): boolean => {
  if (!onboarding) return false;

  return (
    (onboarding.phase !== 'verified' && onboarding.action_required_by_company) ||
    hasOpenTaxDeadline(onboarding)
  );
};

export const openStepCount = (onboarding?: CardOnboardingResponse): number =>
  onboarding?.steps.filter((step) => step.state === 'todo').length ?? 0;
